/**
 * One owned component per data stratum (strict grammar, ruled
 * 2026-08-06): a stratum keeps its identity across every projection
 * the way an Element keeps its identity across surfaces. Screens are
 * FORBIDDEN (by lint) from rendering a stratum any other way -- the
 * typed-layers preference applied to pixels.
 */
import type { ReactNode } from 'react';
import { RADIUS, SPACE, TEXT, usePalette } from '../theme';
import { StrataChip } from './chip';
import { categoryPaint, usePaints, verdictPaint, type VerdictState } from './paints';

/** Compliance fact: the binary verdict. Violating red belongs to
 * this component and the canvas's redline geography -- nothing else. */
export function ComplianceTag({ state, children }: { state: VerdictState; children?: ReactNode }) {
  const paints = usePaints();
  return <StrataChip paint={verdictPaint(paints, state)}>{children ?? state}</StrataChip>;
}

/** Ruleset row: the provenance pill. The tier is the first token of
 * the provenance string; the hint carries the raw citation. */
export function ProvenancePill({ provenance }: { provenance: string }) {
  const paints = usePaints();
  const p = usePalette();
  const tier = provenance.startsWith('retail')
    ? { label: 'retail-cited', paint: paints.verdict.complying }
    : provenance.startsWith('practice')
      ? { label: 'practice-cited', paint: paints.verdict.complying }
      : provenance.startsWith('dev-placeholder')
        ? { label: 'dev-placeholder', paint: paints.absence }
        : { label: 'legacy', paint: p.inkSecondary };
  return (
    <StrataChip paint={tier.paint} title={provenance}>
      {tier.label}
    </StrataChip>
  );
}

/** Absence: NULL is a loud fact, never silence -- and it is NOT a
 * violation, so every absence wears the one amber (UNPRICED,
 * UNVERIFIED, NULL, PROXY...). */
export function AbsenceBadge({ label, reason }: { label: string; reason?: string }) {
  const paints = usePaints();
  return (
    <StrataChip paint={paints.absence} title={reason}>
      {label.toUpperCase()}
    </StrataChip>
  );
}

/** Approval: the standing human fact, always carrying WHO. */
export function ApprovalMark({
  decision,
  who,
}: {
  decision: 'verified' | 'accepted' | 'rejected' | 'ratified';
  who: string;
}) {
  const paints = usePaints();
  const p = usePalette();
  const paint = decision === 'rejected' ? p.inkMuted : paints.verdict.complying;
  return (
    <StrataChip paint={paint}>
      {decision} · {who}
    </StrataChip>
  );
}

/** The suggestion kinds the edit machinery can apply mechanically;
 * everything else is advisory -- informational until a human acts,
 * and NEVER offered the apply path. */
const APPLIABLE_KINDS = new Set(['lower_end_connected', 'translate_down']);
export const isAppliable = (kind: string) => APPLIABLE_KINDS.has(kind);

/** Suggestion: a derived proposal. Advisory kinds announce
 * themselves; appliable kinds let the accept button carry the verb. */
export function SuggestionNote({ kind, description }: { kind: string; description: string }) {
  const paints = usePaints();
  const p = usePalette();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.tight, fontSize: TEXT.caption, color: paints.absence, margin: `${SPACE.xs}px 0` }}>
      {!isAppliable(kind) && <StrataChip paint={p.inkSecondary}>advisory</StrataChip>}
      <span>{description}</span>
    </div>
  );
}

/** Run: the compute's testimony as a rollup. The affirmative claim is
 * first-class -- complying counts render even when nothing violates. */
export function RunRollup({ compliant, breach }: { compliant: number; breach: number }) {
  return (
    <span style={{ display: 'inline-flex', gap: SPACE.tight, flexWrap: 'wrap' }}>
      <ComplianceTag state="complying">{compliant} complying</ComplianceTag>
      {breach > 0 && <ComplianceTag state="violating">{breach} violating</ComplianceTag>}
    </span>
  );
}

/** Element identity: the category dot. Same hue on the canvas, the
 * legend, the overview, and the review queue -- color follows the
 * entity, never the screen. */
export function CategorySwatch({ category, size = 10 }: { category: string; size?: number }) {
  const paints = usePaints();
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'inline-block',
        backgroundColor: categoryPaint(paints, category),
      }}
    />
  );
}

/** A circuit's own hue as a square swatch (the circuits key). */
export function HueSwatch({ color, size = 10 }: { color: string; size?: number }) {
  return (
    <span
      aria-hidden
      style={{ width: size, height: size, borderRadius: RADIUS.hair, flexShrink: 0, display: 'inline-block', backgroundColor: color }}
    />
  );
}
